import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ContentProvider } from "./context/ContentContext";
import { PageTracker } from "./components/PageTracker";

// Layout Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Page Components
import HomePage from "./components/pages/HomePage";
import AboutPage from "./components/pages/AboutPage";
import ProgramsPage from "./components/pages/ProgramsPage";
import EventsPage from "./components/pages/EventsPage";
import MembershipPage from "./components/pages/MembershipPage";
import GetInvolvedPage from "./components/pages/GetInvolvedPage";
import DonatePage from "./components/pages/DonatePage";
import NewsPage from "./components/pages/NewsPage";
import ContactPage from "./components/pages/ContactPage";
import FoundingMembersPage from "./components/pages/FoundingMembersPage";
import GFWCPage from "./components/pages/GFWCPage";
import GalleryPage from "./components/pages/GalleryPage";
import LoginPage from "./components/pages/LoginPage";
import RegisterPage from "./components/pages/RegisterPage";
import ForgotPasswordPage from "./components/pages/ForgotPasswordPage";
import MemberDashboard from "./components/pages/MemberDashboard";
import MemberCalendarPage from "./components/pages/MemberCalendarPage";
import MeetingMinutesPage from "./components/pages/MeetingMinutesPage";
import MemberInfoFormPage from "./components/pages/MemberInfoFormPage";
import MemberDirectoryPage from "./components/pages/MemberDirectoryPage";
import OfficerRosterPage from "./components/pages/OfficerRosterPage";
import BookClubPage from "./components/pages/BookClubPage";
import LeadershipLibraryPage from "./components/pages/LeadershipLibraryPage";
import WelcomePage from "./components/pages/WelcomePage";
import AdminPage from "./components/pages/AdminPage";
import PaymentSuccessPage from "./components/pages/PaymentSuccessPage";
import PaymentCancelPage from "./components/pages/PaymentCancelPage";

// Layout wrapper component
const Layout = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f1e3eb]">
        <p className="text-[#5f6d51]" data-testid="auth-loading">Loading...</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ContentProvider>
        <BrowserRouter>
          <PageTracker />
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/about" element={<Layout><AboutPage /></Layout>} />
            <Route path="/programs" element={<Layout><ProgramsPage /></Layout>} />
            <Route path="/events" element={<Layout><EventsPage /></Layout>} />
            <Route path="/membership" element={<Layout><MembershipPage /></Layout>} />
            <Route path="/get-involved" element={<Layout><GetInvolvedPage /></Layout>} />
            <Route path="/donate" element={<Layout><DonatePage /></Layout>} />
            <Route path="/news" element={<Layout><NewsPage /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
            <Route path="/founding-members" element={<Layout><FoundingMembersPage /></Layout>} />
            <Route path="/gfwc" element={<Layout><GFWCPage /></Layout>} />
            <Route path="/gallery" element={<Layout><GalleryPage /></Layout>} />

            {/* Payments */}
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />

            {/* Auth Pages (no footer) */}
            <Route path="/login" element={<><Header /><LoginPage /></>} />
            <Route path="/register" element={<><Header /><RegisterPage /></>} />
            <Route path="/forgot-password" element={<><Header /><ForgotPasswordPage /></>} />

            {/* Member Portal */}
            <Route
              path="/welcome"
              element={
                <ProtectedRoute>
                  <Header />
                  <WelcomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/member-calendar"
              element={
                <ProtectedRoute>
                  <Header />
                  <MemberCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meeting-minutes"
              element={
                <ProtectedRoute>
                  <Header />
                  <MeetingMinutesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/member-info"
              element={
                <ProtectedRoute>
                  <Header />
                  <MemberInfoFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/member-directory"
              element={
                <ProtectedRoute>
                  <Header />
                  <MemberDirectoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/officers"
              element={
                <ProtectedRoute>
                  <Header />
                  <OfficerRosterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/book-club"
              element={
                <ProtectedRoute>
                  <Header />
                  <BookClubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leadership-library"
              element={
                <ProtectedRoute>
                  <Header />
                  <LeadershipLibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/member-dashboard"
              element={
                <ProtectedRoute>
                  <Header />
                  <MemberDashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Panel */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <Header />
                  <AdminPage />
                </ProtectedRoute>
              }
            />

            {/* Placeholder routes */}
            <Route
              path="/privacy"
              element={
                <Layout>
                  <div className="min-h-screen pt-56 pb-20 px-4">
                    <div className="max-w-3xl mx-auto">
                      <h1 className="font-serif text-4xl text-[#3c4a2a] mb-6">Privacy Policy</h1>
                      <p className="text-[#5f6d51]">Privacy policy content coming soon.</p>
                    </div>
                  </div>
                </Layout>
              }
            />
            <Route
              path="/terms"
              element={
                <Layout>
                  <div className="min-h-screen pt-56 pb-20 px-4">
                    <div className="max-w-3xl mx-auto">
                      <h1 className="font-serif text-4xl text-[#3c4a2a] mb-6">Terms of Service</h1>
                      <p className="text-[#5f6d51]">Terms of service content coming soon.</p>
                    </div>
                  </div>
                </Layout>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </ContentProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
